import { create } from 'zustand';
import { WebSocketHeartbeat } from '../../utils/websocketHeartbeat';

interface WebSocketState {
  tradePrice: string | null;
  tradeQuantity: string | null;
  tradeId: number | null;
  tradeTimestamp: number | null;
  tradeIsBuyerMaker: boolean | null;

  depthAsks: any[] | null;
  depthBids: any[] | null;

  tickerHigh: string | null;
  tickerLow: string | null;
  tickerChange: string | null;
  tickerVolume: string | null;

  markPrice: string | null;
  markPriceIndex: string | null;
  markPriceRate: string | null;
  markPriceTime: string | null;

  setSymbol: (symbol: string) => void;
  closeSocket: () => void;
}

export const useBinanceWebSocketStore = create<WebSocketState>((set, get) => {
  let socket: WebSocket | null = null;
  let currentSymbol = '';
  let activeConsumers = 0;
  let isConnecting = false;
  let isFlushing = false;
  let reconnectAttempts = 0;
  const maxReconnects = 10;
  const FLUSH_THROTTLE_TIME = 50;
  let hasPendingUpdate = false;

  let flushTimer: any = null;
  // let visibilityInterval: any = null;
  let heartbeat: WebSocketHeartbeat | null = null;

  const latestData: {
    tradePrice: string | null;
    tradeQuantity: string | null;
    tradeId: number | null;
    tradeTimestamp: number | null;
    tradeIsBuyerMaker: boolean | null;

    depthAsks: any[] | null;
    depthBids: any[] | null;

    tickerHigh: string | null;
    tickerLow: string | null;
    tickerChange: string | null;
    tickerVolume: string | null;

    markPrice: string | null;
    markPriceIndex: string | null;
    markPriceRate: string | null;
    markPriceTime: string | null;
  } = {
    tradePrice: null,
    tradeQuantity: null,
    tradeId: null,
    tradeTimestamp: null,
    tradeIsBuyerMaker: null,

    depthAsks: null,
    depthBids: null,

    tickerHigh: null,
    tickerLow: null,
    tickerChange: null,
    tickerVolume: null,

    markPrice: null,
    markPriceIndex: null,
    markPriceRate: null,
    markPriceTime: null,
  };


  const flush = () => {
    if (document.visibilityState === 'hidden') return;

    if (!hasPendingUpdate) return;
    hasPendingUpdate = false;

    if (isFlushing) return;
    isFlushing = true;

    const start = performance.now();

    try {
      const prevState = get();
      const updates: Partial<WebSocketState> = {};

      if (prevState.tradePrice !== latestData.tradePrice) updates.tradePrice = latestData.tradePrice;
      if (prevState.tradeQuantity !== latestData.tradeQuantity) updates.tradeQuantity = latestData.tradeQuantity;
      if (prevState.tradeId !== latestData.tradeId) updates.tradeId = latestData.tradeId;
      if (prevState.tradeTimestamp !== latestData.tradeTimestamp) updates.tradeTimestamp = latestData.tradeTimestamp;
      if (prevState.tradeIsBuyerMaker !== latestData.tradeIsBuyerMaker) updates.tradeIsBuyerMaker = latestData.tradeIsBuyerMaker;
      if (prevState.depthAsks !== latestData.depthAsks) updates.depthAsks = latestData.depthAsks;
      if (prevState.depthBids !== latestData.depthBids) updates.depthBids = latestData.depthBids;
      if (prevState.tickerHigh !== latestData.tickerHigh) updates.tickerHigh = latestData.tickerHigh;
      if (prevState.tickerLow !== latestData.tickerLow) updates.tickerLow = latestData.tickerLow;
      if (prevState.tickerChange !== latestData.tickerChange) updates.tickerChange = latestData.tickerChange;
      if (prevState.tickerVolume !== latestData.tickerVolume) updates.tickerVolume = latestData.tickerVolume;
      if (prevState.markPrice !== latestData.markPrice) updates.markPrice = latestData.markPrice;
      if (prevState.markPriceIndex !== latestData.markPriceIndex) updates.markPriceIndex = latestData.markPriceIndex;
      if (prevState.markPriceRate !== latestData.markPriceRate) updates.markPriceRate = latestData.markPriceRate;
      if (prevState.markPriceTime !== latestData.markPriceTime) updates.markPriceTime = latestData.markPriceTime;

      if (Object.keys(updates).length > 0) {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => set(updates), { timeout: 200 });
        } else {
          set(updates);
        }
      }
    } catch (e) {
      console.error('[BinanceWebSocketStore] flush error:', e);
    } finally {
      const duration = performance.now() - start;
      if (duration > 16.67) {
        console.warn(`[BinanceWebSocketStore] flush took issue ${duration.toFixed(2)}ms`);
      } else {
        // console.log(`[BinanceWebSocketStore] flush took ${duration.toFixed(2)}ms`);
      }
      isFlushing = false;
    }
  };


  // const flush = () => {
  //   if (!socket || isFlushing) return;
  //   isFlushing = true;
  //
  //   const prevState = get();
  //   const updates: Partial<WebSocketState> = {};
  //
  //   if (prevState.tradePrice !== latestData.tradePrice) updates.tradePrice = latestData.tradePrice;
  //   if (prevState.tradeQuantity !== latestData.tradeQuantity) updates.tradeQuantity = latestData.tradeQuantity;
  //   if (prevState.tradeId !== latestData.tradeId) updates.tradeId = latestData.tradeId;
  //   if (prevState.tradeTimestamp !== latestData.tradeTimestamp) updates.tradeTimestamp = latestData.tradeTimestamp;
  //   if (prevState.tradeIsBuyerMaker !== latestData.tradeIsBuyerMaker) updates.tradeIsBuyerMaker = latestData.tradeIsBuyerMaker;
  //   if (prevState.depthAsks !== latestData.depthAsks) updates.depthAsks = latestData.depthAsks;
  //   if (prevState.depthBids !== latestData.depthBids) updates.depthBids = latestData.depthBids;
  //   if (prevState.tickerHigh !== latestData.tickerHigh) updates.tickerHigh = latestData.tickerHigh;
  //   if (prevState.tickerLow !== latestData.tickerLow) updates.tickerLow = latestData.tickerLow;
  //   if (prevState.tickerChange !== latestData.tickerChange) updates.tickerChange = latestData.tickerChange;
  //   if (prevState.tickerVolume !== latestData.tickerVolume) updates.tickerVolume = latestData.tickerVolume;
  //   if (prevState.markPrice !== latestData.markPrice) updates.markPrice = latestData.markPrice;
  //   if (prevState.markPriceIndex !== latestData.markPriceIndex) updates.markPriceIndex = latestData.markPriceIndex;
  //   if (prevState.markPriceRate !== latestData.markPriceRate) updates.markPriceRate = latestData.markPriceRate;
  //   if (prevState.markPriceTime !== latestData.markPriceTime) updates.markPriceTime = latestData.markPriceTime;
  //
  //   if (Object.keys(updates).length > 0) {
  //     requestIdleCallback(() => {
  //       set(updates);
  //     });
  //   }
  //
  //   isFlushing = false;
  //   rafPending = false;
  // };


  const scheduleFlush = () => {
    if (document.visibilityState === 'hidden') return;
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      if (document.visibilityState === 'visible') flush();
      flushTimer = null;
    }, FLUSH_THROTTLE_TIME);
  };

  // const scheduleFlush = () => {
  //   if (rafPending || isFlushing) return;
  //   rafPending = true;
  //
  //   const callback = () => {
  //     flush();
  //     rafPending = false; // 중복 예약 방지
  //   };
  //
  //   if (document.visibilityState === 'visible') {
  //     requestAnimationFrame(callback);
  //   } else {
  //     setTimeout(callback, 3000);
  //   }
  // };

  const handleVisibilityChange = () => {
    console.log('[BinanceWebSocketStore] visibility changed:', document.visibilityState);
    if (document.visibilityState === 'visible') {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        setupWebSocket(currentSymbol);
      }
      flush();
    }
  };


  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // if (!hasInitialized && typeof window !== 'undefined') {
  //   hasInitialized = true;
  //   document.addEventListener('visibilitychange', handleVisibilityChange);
  //   visibilityInterval = setInterval(() => {
  //     if (document.visibilityState === 'hidden') {
  //       flush();
  //     }
  //   }, 30000);
  // }

  const setupWebSocket = (symbol: string) => {
    const lowerSymbol = symbol.toLowerCase();
    const isSameSymbol = currentSymbol === lowerSymbol;

    if (isSameSymbol && socket && socket.readyState === WebSocket.OPEN) {
      return;
    }

    isConnecting = true;
    currentSymbol = lowerSymbol;

    if (socket) {
      try {
        socket.onopen = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.onmessage = null;
        socket.close();
        isConnecting = false;
      } catch (e) {
        console.warn('[BinanceWebSocketStore] Socket Error:', e);
      }
      socket = null;
    }

    // ✅ realtime 서버로 연결 (Binance 직접 연결 대신)
    const REALTIME_URL = import.meta.env.VITE_APP_STREAM_URL || 'ws://localhost:8999';
    const ws = new WebSocket(REALTIME_URL);

    ws.onopen = () => {
      isConnecting = false;
      reconnectAttempts = 0;

      // ✅ Heartbeat 시작
      heartbeat = new WebSocketHeartbeat(
        ws,
        () => setupWebSocket(currentSymbol), // Pong 타임아웃 시 재연결
        'BinanceWebSocket'
      );
      heartbeat.start(30000, 5000);

      // realtime 서버에 구독 요청 전송
      ws.send(JSON.stringify({
        type: 'subscribe',
        symbol: currentSymbol,
        channel: 'all' // aggTrade, depth, ticker, markPrice 모두 구독
      }));
      console.log('[BinanceWebSocketStore] Subscribed to realtime server:', currentSymbol);
    };


    ws.onclose = () => {
      isConnecting = false;
      console.warn('[BinanceWebSocketStore] WebSocket closed');

      // ✅ Heartbeat 정리
      heartbeat?.stop();
      heartbeat = null;

      if (reconnectAttempts < maxReconnects) {
        reconnectAttempts++;
        setTimeout(() => setupWebSocket(currentSymbol), 5000);
      } else {
        console.warn('[BinanceWebSocketStore] reconnect stopped after max attempts');
      }
    };

    ws.onmessage = (event) => {
      if (document.visibilityState !== 'visible') {
        hasPendingUpdate = true;
        return;
      }

      try {
        const message = JSON.parse(event.data);

        // ✅ Heartbeat: Pong 메시지 처리
        if (message.type === 'pong' || message.channel === 'pong') {
          heartbeat?.receivedPong();
          return;
        }

        // realtime 서버 응답 처리 (success/error/info 메시지는 스킵)
        if (message.success || message.error || message.info) {
          console.log('[BinanceWebSocketStore]', message);
          return;
        }

        // 실시간 데이터 처리: realtime 서버는 { channel, data } 형식으로 전송
        const { channel, data: rawData } = message;
        if (!channel || !rawData) return;

        // Redis에서 온 데이터는 JSON 문자열이므로 한 번 더 파싱 필요
        const data = JSON.parse(rawData);

        // 채널 타입별 데이터 처리
        if (channel.includes(':aggTrade') || channel.includes(':trade')) {
          latestData.tradePrice = data.p;
          latestData.tradeQuantity = data.q;
          latestData.tradeId = data.t;
          latestData.tradeTimestamp = data.T;
          latestData.tradeIsBuyerMaker = data.m;
        } else if (channel.includes(':depth')) {
          latestData.depthAsks = data.a;
          latestData.depthBids = data.b;
        } else if (channel.includes(':ticker')) {
          latestData.tickerHigh = data.h;
          latestData.tickerLow = data.l;
          latestData.tickerChange = data.P;
          latestData.tickerVolume = data.q;
        } else if (channel.includes(':markPrice')) {
          latestData.markPrice = data.p;
          latestData.markPriceIndex = data.i;
          latestData.markPriceRate = data.r;
          latestData.markPriceTime = data.T;
        }

        hasPendingUpdate = true;
        scheduleFlush();

      } catch (err) {
        console.error('[BinanceWebSocketStore] message parse error:', err);
      }
    };

    ws.onerror = (err) => {
      console.error('[BinanceWebSocketStore] WebSocket error:', err);
    };

    socket = ws;
  };

  return {
    tradePrice: null,
    tradeQuantity: null,
    tradeId: null,
    tradeTimestamp: null,
    tradeIsBuyerMaker: null,
    depthAsks: null,
    depthBids: null,
    tickerHigh: null,
    tickerLow: null,
    tickerChange: null,
    tickerVolume: null,
    markPrice: null,
    markPriceIndex: null,
    markPriceRate: null,
    markPriceTime: null,
    setSymbol: (symbol) => {
      if (!symbol) return;
      setupWebSocket(symbol);
      activeConsumers++;
    },
    closeSocket: () => {
      if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }

      activeConsumers = Math.max(0, activeConsumers - 1);
      if (activeConsumers === 0 && socket) {
        try {
          // ✅ Heartbeat 정리
          heartbeat?.stop();
          heartbeat = null;

          // realtime 서버에 구독 해제 요청 전송
          if (socket.readyState === WebSocket.OPEN && currentSymbol) {
            socket.send(JSON.stringify({
              type: 'unsubscribe',
              symbol: currentSymbol,
              channel: 'all'
            }));
            console.log('[BinanceWebSocketStore] Unsubscribed from realtime server:', currentSymbol);
          }

          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          socket.close();
          isConnecting = false;
        } catch (e) {
          console.warn('[BinanceWebSocketStore] Error closing socket:', e);
        }
        socket = null;

        // if (visibilityInterval) {
        //   clearInterval(visibilityInterval);
        //   visibilityInterval = null;
        // }
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        latestData.tradePrice = null;
        latestData.tradeQuantity = null;
        latestData.tradeId = null;
        latestData.tradeTimestamp = null;
        latestData.tradeIsBuyerMaker = null;
        latestData.depthAsks = null;
        latestData.depthBids = null;
        latestData.tickerHigh = null;
        latestData.tickerLow = null;
        latestData.tickerChange = null;
        latestData.tickerVolume = null;
        latestData.markPrice = null;
        latestData.markPriceIndex = null;
        latestData.markPriceRate = null;
        latestData.markPriceTime = null;
      }
    },
  };
});
